import { useState, type ChangeEvent, type MouseEvent } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import Button from "@mui/material/Button";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GridViewIcon from "@mui/icons-material/GridView";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import UploadIcon from "@mui/icons-material/UploadFile";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";

export type ExplorerViewMode = "list" | "grid";

// A shared compact height so Upload, Actions, Search and View all line up
// (coordinator review: the Search field was taller than the buttons).
const TOOLBAR_CONTROL_HEIGHT = 36;

export interface ExplorerToolbarProps {
  uploadEnabled: boolean;
  onUpload: () => void;
  /** ADR 0010: shown next to Upload only where a mount supports folders (omitted elsewhere). */
  newFolderEnabled?: boolean;
  onNewFolder?: () => void;
  canDownloadSelected: boolean;
  canDeleteSelected: boolean;
  showDeleteAction: boolean;
  onDownloadSelected: () => void;
  onDeleteSelected: () => void;
  onRefresh: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  view: ExplorerViewMode;
  onToggleView: () => void;
}

/**
 * The explorer's toolbar row (files-explorer-reference.png): Upload,
 * an Actions dropdown (Download selected, Delete selected, Refresh),
 * a client-side Search box, and a list/grid View toggle.
 */
export function ExplorerToolbar({
  uploadEnabled,
  onUpload,
  newFolderEnabled,
  onNewFolder,
  canDownloadSelected,
  canDeleteSelected,
  showDeleteAction,
  onDownloadSelected,
  onDeleteSelected,
  onRefresh,
  search,
  onSearchChange,
  view,
  onToggleView,
}: ExplorerToolbarProps) {
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);

  function openActions(event: MouseEvent<HTMLElement>) {
    setActionsAnchor(event.currentTarget);
  }

  function closeActions() {
    setActionsAnchor(null);
  }

  function handleSearch(event: ChangeEvent<HTMLInputElement>) {
    onSearchChange(event.target.value);
  }

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        rowGap: 1,
        columnGap: 1,
        px: 2,
        py: 1.5,
      }}
    >
      {/* Left group: Upload, Actions (files-explorer-reference.png). */}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Button
          size="small"
          startIcon={<UploadIcon fontSize="small" />}
          onClick={onUpload}
          disabled={!uploadEnabled}
          sx={{ height: TOOLBAR_CONTROL_HEIGHT }}
        >
          Upload
        </Button>

        {onNewFolder && (
          <Button
            size="small"
            startIcon={<CreateNewFolderIcon fontSize="small" />}
            onClick={onNewFolder}
            disabled={!newFolderEnabled}
            sx={{ height: TOOLBAR_CONTROL_HEIGHT }}
          >
            New folder
          </Button>
        )}

        <Button
          size="small"
          endIcon={<ExpandMoreIcon fontSize="small" />}
          onClick={openActions}
          sx={{ height: TOOLBAR_CONTROL_HEIGHT }}
        >
          Actions
        </Button>
        <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={closeActions}>
          <MenuItem
            disabled={!canDownloadSelected}
            onClick={() => {
              closeActions();
              onDownloadSelected();
            }}
          >
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            Download selected
          </MenuItem>
          {showDeleteAction && (
            <MenuItem
              disabled={!canDeleteSelected}
              onClick={() => {
                closeActions();
                onDeleteSelected();
              }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete selected
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              closeActions();
              onRefresh();
            }}
          >
            <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
            Refresh
          </MenuItem>
        </Menu>
      </Stack>

      {/* Right group: Search, View (files-explorer-reference.png), full
          width and wrapping to its own row on narrow screens. */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", width: { xs: "100%", sm: "auto" } }}
      >
        <TextField
          size="small"
          placeholder="Search"
          value={search}
          onChange={handleSearch}
          sx={{
            flexGrow: { xs: 1, sm: 0 },
            "& .MuiInputBase-root": { height: TOOLBAR_CONTROL_HEIGHT },
          }}
          slotProps={{
            htmlInput: { "aria-label": "Search this folder" },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <ToggleButton
          value={view}
          selected={false}
          onClick={onToggleView}
          size="small"
          aria-label={view === "list" ? "Switch to grid view" : "Switch to list view"}
          sx={{ height: TOOLBAR_CONTROL_HEIGHT }}
        >
          {view === "list" ? <ViewListIcon fontSize="small" /> : <GridViewIcon fontSize="small" />}
          &nbsp;View
        </ToggleButton>
      </Stack>
    </Stack>
  );
}
